import React, { useCallback, useEffect, useState } from 'react';
import { Subject, lastValueFrom } from 'rxjs';
import { filter, share, take, takeUntil, timeout } from 'rxjs/operators';

import { subscribe } from '@swf/core/api/subscribe';
import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';

import type { DepositResult, FDMS3DS2Info } from '../../../../../../../../types/Cashier';
import { DepositStatus } from '../../../../../../../../types/status';
import { DDCStatus } from '../../RejectedX3dSecure2RequiredError';
import DDCStatusMessages from '../DDCStatusMessages/DDCStatusMessages';

const Fdms: React.FC<DepositResult & { updateFdmsStatus: () => void }> = ({
  resultInfo,
  transactionId,
  updateFdmsStatus
}) => {
  const [resultDDCStatus, setResultDDCStatus] = useState<DDCStatus>();
  const [appended, setAppended] = useState(false);
  const jwt = resultInfo as FDMS3DS2Info;
  const { data } = useWizardContext();
  const request = useRequest();
  const { jumpForward } = useWizardJump();

  const appendScript = useCallback(() => {
    const secureTradingScript = document.createElement('div');

    secureTradingScript.innerHTML = jwt;
    document.body.appendChild(secureTradingScript);
  }, [jwt]);

  useEffect(() => {
    if (!jwt || !transactionId) {
      setResultDDCStatus(DDCStatus.ddcError);
    } else {
      // TODO: TBC if ddcScript and fdmsNotify3dFormCompleted still needed since PYR-150755 bypassing DDC stage
      if (!appended) {
        setResultDDCStatus(DDCStatus.ddcOk);
        appendScript();
        setAppended(true);
      } else {
        request({
          service: 'wc3.paysystem.fdmsNotify3dFormCompleted',
          body: { transactionId }
        });

        const subject = new Subject();
        const depositResponseSubscription = subscribe('/user/queue/cashier').pipe(
          takeUntil(subject),
          share()
        );

        const firstResponse = lastValueFrom(
          depositResponseSubscription.pipe(
            filter(
              ({ body: { status } }) =>
                status !== DepositStatus.PARTIALLY_COMPLETED_CHECK &&
                status !== DepositStatus.REJECTED_X3D_SECURE2_REQUIRED_ERROR
            ),
            take(1)
          )
        );
        const timedOut = lastValueFrom(depositResponseSubscription.pipe(timeout(15000)));

        Promise.race([firstResponse, timedOut]).then(
          ({ body }) => {
            subject.complete();
            if (body.status) {
              data.depositResult = body;
              updateFdmsStatus();
            }
          },
          () => setResultDDCStatus(DDCStatus.ddcTimeout)
        );
      }
    }
  }, [appendScript, appended, data, jumpForward, jwt, request, transactionId, updateFdmsStatus]);

  return <DDCStatusMessages ddcStatus={resultDDCStatus} />;
};

export default Fdms;
